import FormFieldComponent from '@/components/FormFieldComponent';
import ToolBar from '@/components/ToolBar';
import { Button } from '@/components/ui/button';
import { capitalizeEveryWord, capitalizeFirstLetter, formatPhoneNumber, stringToHexColor } from '@/lib/utils';
import { Crown, Edit3 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { ZIP_CODE_VALIDATION } from '@/utils/constant';

import Loader from '@/components/Loader';
import { useUserStore } from '@/stores/user';
import { useGetUserById } from '@/services/userService';

export const Profile = () => {
  const user = useUserStore((state) => state.user);
  const [displayChangePasswordDialog, setDisplayChangePasswordDialog] = useState(false);
  const form = useForm();
  const disabledStyle = 'disabled:opacity-100 text-black';
  const { data: userData, isFetching } = useGetUserById(user?.userId);

  const { control } = form;
  const formFieldClasses = 'bg-[#F5F5F5]rounded-md';

  const data = useMemo(
    () => [
      { label: 'First Name', name: 'firstName', defaultValue: userData?.firstName },
      { label: 'Last Name', name: 'lastName', defaultValue: userData?.lastName },
      { label: 'User ID', name: 'code', defaultValue: userData?.code },
      { label: 'Email Address', name: 'email', defaultValue: userData?.email },
      { label: 'Role', name: 'roleId', defaultValue: capitalizeFirstLetter(userData?.roleId) },
      {
        label: 'Team(s)',
        name: 'teamIds',
        defaultValue: userData?.teams?.map((team) => capitalizeEveryWord(team.name)).join(', '),
      },
    ],
    [userData]
  );

  const supervisorsFor = useMemo(
    () => userData?.teams.filter((team) => team.supervisorId === user?.userId),
    [userData]
  );

  return (
    <FormProvider {...form}>
      <ToolBar title="Profile" />
      <Loader isLoading={isFetching}>
        <div className="z-0 flex w-[100%] flex-col gap-8">
          <div className="flex flex-row justify-between">
            <div className="flex flex-row gap-3">
              <div
                className={`flex items-center justify-center rounded-full w-[48px] h-[48px] text-xl hover:bg-[#D72222] focus:outline-none focus-visible:outline-none text-white p-2.5`}
                style={{ background: stringToHexColor(userData?.firstName) }}
              >
                {userData?.firstName?.charAt(0)}
              </div>
              <div className="flex flex-col">
                <span className="text-base font-medium">{userData?.firstName + ' ' + userData?.lastName}</span>
                <span className="text-sm">#{userData?.code}</span>
                {!!supervisorsFor?.length && (
                  <div className="flex gap-2">
                    <Crown className="text-[#FFAD0D] h-4 w-5" />
                    <span className="text-xs">
                      Supervisor - {supervisorsFor.map((team) => capitalizeEveryWord(team.name)).join(', ')}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              className="h-9 w-fit flex flex-row gap-2"
              onClick={() => {
                setDisplayChangePasswordDialog(true);
              }}
            >
              <Edit3 className="text-primary w-4 h-4" />
              <span className="text-primary">Change Password</span>
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-6">
            {data.map((item, index) => (
              <FormFieldComponent
                key={index}
                control={control}
                name={item.name}
                label={item.label}
                defaultValue={item.defaultValue}
                disabled={true}
                required
                disabledStyle={disabledStyle}
                className="bg-[#F5F5F5]rounded-md"
              />
            ))}
          </div>
          {userData?.roleId === 'SERVER' && (
            <div className="flex flex-col gap-8">
              <span className="w-full text-2xl font-medium">Additional Information</span>
              <div className="flex flex-col gap-6">
                <div className="grid grid-cols-2 gap-6">
                  <FormFieldComponent
                    control={control}
                    name="secondaryEmail"
                    label="Secondary Email Address"
                    defaultValue={userData.secondaryEmail!}
                    placeholder="Enter Secondary Email Address"
                    disabled={true}
                    required
                    disabledStyle={disabledStyle}
                    className={userData.secondaryEmail! ?? formFieldClasses}
                  />
                  <FormFieldComponent
                    control={control}
                    name="phoneNumber"
                    label="Phone Number"
                    defaultValue={formatPhoneNumber(userData.phone) ?? null}
                    required
                    placeholder=""
                    disabled={true}
                    disabledStyle={disabledStyle}
                    className={userData.phone ?? formFieldClasses}
                  />
                </div>
                <FormFieldComponent
                  control={control}
                  name="addressLine1"
                  label="Address Line 1"
                  defaultValue={userData.primaryAddress!}
                  required
                  placeholder="Enter Address Line 1"
                  disabled={true}
                  disabledStyle={disabledStyle}
                  className={userData.primaryAddress! ?? formFieldClasses}
                />
                <FormFieldComponent
                  control={control}
                  name=""
                  label="Address Line 2"
                  defaultValue={userData.secondaryAddress!}
                  disabled={true}
                  disabledStyle={disabledStyle}
                  className={userData.secondaryAddress! ?? formFieldClasses}
                  placeholder="Enter Apt, suite etc. (Optional)"
                />
                <div className="grid grid-cols-3 gap-6">
                  <FormFieldComponent
                    control={control}
                    name="city"
                    label="City"
                    defaultValue={userData.city!}
                    required
                    disabled={true}
                    disabledStyle={disabledStyle}
                    placeholder="City"
                    className={userData.city! ?? formFieldClasses}
                  />
                  <FormFieldComponent
                    control={control}
                    name="state"
                    label="State"
                    defaultValue={userData.state!}
                    required
                    disabled={true}
                    disabledStyle={disabledStyle}
                    className={userData.state! ?? formFieldClasses}
                    placeholder="State"
                  />
                  <FormFieldComponent
                    control={control}
                    name="zipCode"
                    label="Zip Code"
                    defaultValue={userData.zipCode!}
                    required
                    placeholder="Zip Code"
                    disabled={true}
                    disabledStyle={disabledStyle}
                    className={userData.zipCode! ?? formFieldClasses}
                    {...ZIP_CODE_VALIDATION}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </Loader>
      {displayChangePasswordDialog && <ChangePasswordDialog onClose={() => setDisplayChangePasswordDialog(false)} />}
    </FormProvider>
  );
};
